/**
 * CelebrationOverlay — full-screen branded success moment.
 *
 * Used at conversion milestones (booking confirmed, listing published,
 * verification approved, review submitted). Plays a branded animated
 * checkmark + double-tap haptic, then fades out and fires `onComplete`
 * after ~1.6s total. The screen behind dims to a soft purple wash so the
 * checkmark reads cleanly without yanking the user out of context.
 *
 * Why this and not a Lottie:
 *   1. It keeps the animation in code, so there are no bundled placeholder
 *      assets, licensing gaps, or platform-specific Lottie render quirks.
 *   2. It reuses the animation stack the app already ships
 *      (Reanimated + SVG).
 *   3. Web parity is automatic.
 *
 * @example
 * const [done, setDone] = useState(false)
 * if (done) return <CelebrationOverlay
 *   message={t('booking.confirmed')}
 *   onComplete={() => router.replace('/trips')}
 * />
 */

import { useCallback, useEffect, useRef } from 'react'
import { View, Text, type ViewStyle } from 'react-native'
import { AppModal } from './AppModal'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  withSequence,
  useReducedMotion,
  runOnJS,
  Easing,
} from 'react-native-reanimated'
import { LinearGradient } from 'expo-linear-gradient'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { notification, NotificationType } from '../../lib/utils/haptics'
import { COLORS, FONTS, SPACING } from '../../lib/brand'
import { TEXT_STYLES, DURATION } from '../../lib/theme/tokens'
import { AnimatedCheckmark } from './AnimatedCheckmark'

interface CelebrationOverlayProps {
  /** Headline shown under the checkmark. Required — celebrate something specific. */
  message: string
  /** Optional secondary text. */
  subMessage?: string
  /** Fires after the full sequence (~1600ms typical, ~0ms with reduced motion). */
  onComplete: () => void
  /** Override total duration before onComplete fires. Default 1600ms. */
  duration?: number
  /** Override checkmark color. Defaults to coral. */
  checkColor?: string
  testID?: string
}

const FADE_IN_MS = 220
const FADE_OUT_MS = 280
const DEFAULT_TOTAL_MS = 1600

/**
 * Grace period after the sequence should have ended, before the JS backstop
 * fires `onComplete` itself.
 *
 * This component is a full-screen `AppModal` with `visible` hardcoded true,
 * `pointerEvents="auto"`, and NO close control of any kind — the parent
 * unmounting it in response to `onComplete` is the only thing that can ever
 * take it down. `onComplete` used to be delivered exclusively from a Reanimated
 * completion callback on the UI thread. A dropped or `finished: false` callback
 * therefore left a fully transparent (opacity has already animated to 0),
 * fully mounted, touch-eating full-screen modal over the app: the user sees the
 * app, taps it, and nothing happens. Android hardware back escapes via
 * `onRequestClose`; iOS and mobile web have nothing.
 *
 * A plain JS timer cannot be dropped by the animation system, so dismissal no
 * longer depends on the animation landing at all.
 */
const COMPLETE_BACKSTOP_GRACE_MS = 1200

export function CelebrationOverlay({
  message,
  subMessage,
  onComplete,
  duration = DEFAULT_TOTAL_MS,
  checkColor = COLORS.coral.DEFAULT,
  testID = 'celebration-overlay',
}: CelebrationOverlayProps) {
  const reducedMotion = useReducedMotion()
  const insets = useSafeAreaInsets()

  const overlayOpacity = useSharedValue(0)
  const messageOpacity = useSharedValue(0)
  const messageY = useSharedValue(8)

  // `onComplete` must fire exactly once, from whichever source gets there
  // first: the animation callback, the backstop timer, or Android back.
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])
  const hasCompletedRef = useRef(false)
  const fireComplete = useCallback(() => {
    if (hasCompletedRef.current) return
    hasCompletedRef.current = true
    onCompleteRef.current()
  }, [])

  // The backstop. Independent of Reanimated, of `finished`, and of the
  // reduced-motion branch — see COMPLETE_BACKSTOP_GRACE_MS.
  useEffect(() => {
    const timer = setTimeout(fireComplete, duration + COMPLETE_BACKSTOP_GRACE_MS)
    return () => clearTimeout(timer)
  }, [duration, fireComplete])

  useEffect(() => {
    // Haptic on enter — double-tap "success" feel.
    notification(NotificationType.Success)

    if (reducedMotion) {
      // Reduced motion: snap visible, hold briefly, fire complete.
      overlayOpacity.value = 1
      messageOpacity.value = 1
      messageY.value = 0
      const timer = setTimeout(fireComplete, Math.min(duration, 800))
      return () => clearTimeout(timer)
    }

    overlayOpacity.value = withSequence(
      withTiming(1, { duration: FADE_IN_MS, easing: Easing.out(Easing.cubic) }),
      withDelay(
        Math.max(0, duration - FADE_IN_MS - FADE_OUT_MS),
        // No `if (finished)` guard. An interrupted fade-out is exactly the case
        // that must still dismiss — `fireComplete` is idempotent, so racing the
        // backstop timer is harmless, and gating on `finished` was what made
        // an interruption leave the lid up.
        withTiming(0, { duration: FADE_OUT_MS, easing: Easing.in(Easing.cubic) }, () => {
          runOnJS(fireComplete)()
        }),
      ),
    )
    messageOpacity.value = withDelay(
      460,
      withTiming(1, { duration: DURATION.fast, easing: Easing.out(Easing.cubic) }),
    )
    messageY.value = withDelay(
      460,
      withTiming(0, { duration: DURATION.fast, easing: Easing.out(Easing.cubic) }),
    )
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reducedMotion])

  const overlayStyle = useAnimatedStyle(() => ({
    opacity: overlayOpacity.value,
  }))

  const messageStyle = useAnimatedStyle(() => ({
    opacity: messageOpacity.value,
    transform: [{ translateY: messageY.value }],
  }))

  const wrapperStyle: ViewStyle = {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: SPACING.xl,
    paddingTop: insets.top,
    paddingBottom: insets.bottom,
  }

  // Fire a second haptic 300ms after the first for the "double-tap" pattern.
  // Done outside Reanimated so it isn't gated on reducedMotion.
  useEffect(() => {
    const t = setTimeout(() => notification(NotificationType.Success), 300)
    return () => clearTimeout(t)
  }, [])

  return (
    <AppModal
      transparent
      animationType="none"
      statusBarTranslucent
      visible
      onRequestClose={fireComplete}
      testID={testID}
    >
      <Animated.View
        style={[
          {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
          overlayStyle,
        ]}
        pointerEvents="auto"
      >
        <LinearGradient
          colors={['rgba(45, 42, 94, 0.96)', 'rgba(29, 26, 78, 0.98)', 'rgba(17, 14, 28, 0.99)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ flex: 1 }}
        >
          <View style={wrapperStyle}>
            <AnimatedCheckmark size={140} color={checkColor} filled />

            <Animated.View style={[{ marginTop: SPACING.xl, alignItems: 'center' }, messageStyle]}>
              <Text
                style={{
                  // Heavitas — celebration moments are the highest-stakes
                  // brand impressions in the app (booking confirmed, listing
                  // published, KYC approved). The brand display face turns a
                  // generic success modal into a Tago-specific moment.
                  ...TEXT_STYLES.display,
                  fontSize: 26,
                  lineHeight: 32,
                  color: '#FFFFFF',
                  textAlign: 'center',
                }}
                accessibilityRole="header"
              >
                {message}
              </Text>
              {subMessage ? (
                <Text
                  style={{
                    marginTop: SPACING.sm,
                    fontFamily: FONTS.body,
                    fontSize: 14,
                    color: 'rgba(255,255,255,0.78)',
                    textAlign: 'center',
                    maxWidth: 280,
                    lineHeight: 20,
                  }}
                >
                  {subMessage}
                </Text>
              ) : null}
            </Animated.View>
          </View>
        </LinearGradient>
      </Animated.View>
    </AppModal>
  )
}
